/**
 * One user. Spec 11.4: "view sessions, credits, subscription; suspend; change role; grant
 * credits."
 *
 * Every action here needs a reason and a confirmation, for the same argument spec 11.3
 * makes about facilitator overrides: an administrator who can act on someone's account
 * without saying why produces a record nobody can reconstruct later. The reason goes into
 * `audit_logs`, and the dialog says so before it is submitted.
 *
 * Spec 11.5: no reflection text. The server does not send it and this screen has no way to
 * ask — `reflectionsVisible` comes back `false` and is displayed as a statement, so the
 * absence reads as a rule rather than as a bug.
 */
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Dialog } from '../../../components/ui/Dialog';
import { Field } from '../../../components/ui/Field';
import { SelectField } from '../../../components/ui/FieldParts';
import { SkeletonList } from '../../../components/ui/Skeleton';
import {
  useAdminUser,
  useGrantCredits,
  useSetRole,
  useSuspendUser,
} from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/users/$id')({ component: Page });

type Action = 'suspend' | 'unsuspend' | 'role' | 'grant';

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common', 'billing']);
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const detail = useAdminUser(id);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [role, setRole] = useState<'player' | 'facilitator' | 'platform_admin'>('player');
  const [kind, setKind] = useState<'facilitated' | 'solo'>('facilitated');
  const [delta, setDelta] = useState('5');

  const suspend = useSuspendUser();
  const setUserRole = useSetRole();
  const grant = useGrantCredits();

  const pending = suspend.isPending || setUserRole.isPending || grant.isPending;
  const error = suspend.error ?? setUserRole.error ?? grant.error;

  const close = (): void => {
    setAction(null);
    setReason('');
  };

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    close();
  };

  const submit = (): void => {
    if (reason.trim() === '') return;

    switch (action) {
      case 'suspend':
      case 'unsuspend':
        suspend.mutate(
          { userId: id, suspended: action === 'suspend', reason: reason.trim() },
          { onSuccess: refresh },
        );
        return;
      case 'role':
        setUserRole.mutate({ userId: id, role, reason: reason.trim() }, { onSuccess: refresh });
        return;
      case 'grant':
        grant.mutate(
          { userId: id, kind, delta: Number(delta), reason: reason.trim() },
          { onSuccess: refresh },
        );
        return;
      case null:
        return;
    }
  };

  if (detail.error !== null) {
    return <Alert tone="error">{t(errorKeyFor(detail.error))}</Alert>;
  }
  if (detail.isLoading || detail.data === undefined) {
    return <SkeletonList rows={4} label={t('admin:nav.users')} />;
  }

  const { user, credits, subscription, reflectionsVisible } = detail.data;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{user.displayName}</h1>
          <p className="text-sm text-ink-muted">{user.email ?? t('admin:users.guest')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={user.role === 'platform_admin' ? 'warning' : 'neutral'}>{user.role}</Badge>
          {user.suspendedAt !== null && <Badge tone="danger">{t('admin:users.suspended')}</Badge>}
          {user.isGuest && <Badge tone="neutral">{t('admin:users.guest')}</Badge>}
        </div>
      </header>

      {/* Spec 11.5, stated rather than implied. */}
      {!reflectionsVisible && <Alert tone="info">{t('admin:users.reflectionsSealed')}</Alert>}

      {error !== null && <Alert tone="error">{t(errorKeyFor(error))}</Alert>}

      <Card title={t('billing:credits')}>
        <dl className="flex flex-wrap gap-6 text-sm">
          <div>
            <dt className="text-xs text-ink-muted">{t('billing:creditsFacilitated')}</dt>
            <dd className="text-lg tabular-nums text-ink">{credits.facilitated}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('billing:creditsSolo')}</dt>
            <dd className="text-lg tabular-nums text-ink">{credits.solo}</dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">{t('billing:plan')}</dt>
            <dd className="text-lg text-ink">{subscription?.planKey ?? t('billing:noPlan')}</dd>
          </div>
        </dl>
      </Card>

      <Card title={t('admin:nav.users')}>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setAction(user.suspendedAt === null ? 'suspend' : 'unsuspend');
            }}
          >
            {user.suspendedAt === null ? t('admin:users.suspend') : t('admin:users.unsuspend')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setRole(user.role);
              setAction('role');
            }}
          >
            {t('admin:users.setRole')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setAction('grant');
            }}
          >
            {t('admin:users.grantCredits')}
          </Button>
        </div>
        <p className="mt-2 text-xs text-ink-muted">
          {new Date(user.createdAt).toLocaleString(i18n.language)}
        </p>
      </Card>

      <Dialog
        open={action !== null}
        title={t('admin:confirm.title')}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('admin:confirm.cancel')}
            </Button>
            <Button loading={pending} disabled={reason.trim() === ''} onClick={submit}>
              {t('admin:confirm.confirm')}
            </Button>
          </>
        }
      >
        {action === 'role' && (
          <SelectField
            label={t('admin:users.role')}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as typeof role);
            }}
            options={[
              { value: 'player', label: 'player' },
              { value: 'facilitator', label: 'facilitator' },
              { value: 'platform_admin', label: 'platform_admin' },
            ]}
          />
        )}

        {action === 'grant' && (
          <>
            <SelectField
              label={t('billing:credits')}
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as typeof kind);
              }}
              options={[
                { value: 'facilitated', label: t('billing:creditsFacilitated') },
                { value: 'solo', label: t('billing:creditsSolo') },
              ]}
            />
            <Field
              label={t('admin:users.grantCredits')}
              type="number"
              min={1}
              max={1000}
              value={delta}
              onChange={(event) => {
                setDelta(event.target.value);
              }}
            />
          </>
        )}

        <Field
          label={t('admin:ruleset.reason')}
          hint={t('admin:confirm.reasonRequired')}
          required
          maxLength={500}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </Dialog>
    </div>
  );
}
